import { useRef } from "react";
import { Button, CircularProgress } from "@mui/material";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { toast } from "react-toastify";
import PhotoCamera from "@mui/icons-material/PhotoCamera";

const PhotoSelector = ({ onSelect, hasPhoto }) => {
  const fileInputRef = useRef(null);
  const maxSize = 3 * 1024 * 1024;

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file", {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }

      if (file.size > maxSize) {
        toast.error(
          "File size exceeds the 3MB limit. Please select a smaller file.",
          {
            position: "top-center",
            autoClose: 3000,
          }
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          onSelect(reader.result);
          toast.success("Photo selected successfully", {
            position: "top-center",
            autoClose: 1000,
          });
        } catch (error) {
          toast.error("Error processing image", {
            position: "top-center",
            autoClose: 3000,
          });
        }
      };
      reader.onerror = () => {
        toast.error("Error reading file", {
          position: "top-center",
          autoClose: 3000,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  return (
    <>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <Button
        variant="outlined"
        onClick={handleButtonClick}
        startIcon={<PhotoCamera />}
      >
        {hasPhoto ? "Change Picture" : "Upload Picture"}
      </Button>
    </>
  );
};

export const FileSelector = ({
  onSelect,
  isLoading = false,
  buttonLabel = "Upload File",
  fullWidth = false,
}) => {
  const fileInputRef = useRef(null);
  const maxSize = 3 * 1024 * 1024;

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      if (file.type != "application/pdf") {
        toast.error("Invalid file type. Please select a PDF file.", {
          position: "top-center",
        });
        return;
      }

      if (file.size > maxSize) {
        toast.error(
          "File size exceeds the 3MB limit. Please select a smaller file.",
          { position: "top-center" }
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        onSelect(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  return (
    <>
      <input
        type="file"
        accept="application/pdf"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <Button
        variant="contained"
        onClick={handleButtonClick}
        color="primary"
        disabled={isLoading}
        startIcon={
          isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <PostAddIcon />
          )
        }
        fullWidth={fullWidth}
      >
        {isLoading ? "Uploading..." : buttonLabel}
      </Button>
    </>
  );
};

export default PhotoSelector;
